import React from "react";
import {
  Box,
  Card,
  Typography,
  alpha,
  SxProps,
  Theme,
  CircularProgress,
  Alert,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { useTheme } from "@mui/material";
import { MonthlySpendTrendData } from "../hooks/useMonthlySpendTrend";

interface MonthlySpendTrendChartProps {
  data: MonthlySpendTrendData[];
  portfolioNames: string[];
  isLoading?: boolean;
  isError?: boolean;
  error?: string;
}

const cardStyles: SxProps<Theme> = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
};

const getPortfolioColor = (index: number, theme: Theme): string => {
  const colors = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.secondary.main,
  ];
  return colors[index % colors.length];
};

export const MonthlySpendTrendChart: React.FC<MonthlySpendTrendChartProps> = ({
  data,
  portfolioNames,
  isLoading = false,
  isError = false,
  error,
}) => {
  const theme = useTheme();

  const series = portfolioNames.map((portfolioName, index) => ({
    data: data.map((item) => item[portfolioName] as number),
    label: portfolioName,
    color: getPortfolioColor(index, theme),
    curve: "monotoneX" as const,
  }));

  return (
    <Box sx={{ p: 2, flex: 1 }}>
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 250,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error || "Failed to load monthly spend trend"}
        </Alert>
      ) : data.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 250,
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">No data available</Typography>
        </Box>
      ) : (
        <Box sx={{ height: 250, width: "100%" }}>
          <LineChart
            xAxis={[
              {
                scaleType: "point",
                data: data.map((item) => item.month),
                tickLabelStyle: { fontSize: 12 },
                height: 30,
              },
            ]}
            yAxis={[
              {
                tickLabelStyle: { fontSize: 12 },
                valueFormatter: (value: number) => `£${value.toFixed(1)}M`,
                width: 60,
              },
            ]}
            series={series}
            slotProps={{
              legend: {
                position: { vertical: "top", horizontal: "end" },
              },
            }}
            grid={{ vertical: true, horizontal: true }}
            margin={{ right: 10, left: 10, top: 0, bottom: 0 }}
            hideLegend
          />
        </Box>
      )}
    </Box>
  );
};
